import {
  MetricsV1AggregationThresholdInput,
  MetricsV1AggregationType,
  MetricsV1FilterInput,
  MetricsV1ValueCapInput,
} from '@spotify/confidence-graphql';

import { aggregationUtils } from '../../Aggregation';
import { applyCap, coalesceToZeroIf, filterToWhereClause } from './sqlHelpers';

export type AverageCodeSnippetProps = {
  entity?: string;
  measurement?: string;
  aggregationMethod?: MetricsV1AggregationType;
  factTable?: string;
  aggregationThreshold?: Maybe<MetricsV1AggregationThresholdInput>;
  aggregationCap?: Maybe<MetricsV1ValueCapInput>;
  replaceMissingWithZero?: boolean;
  filter?: Maybe<MetricsV1FilterInput>;
};

export const AVERAGE_KEYS: ReadonlyArray<keyof AverageCodeSnippetProps> = [
  'entity',
  'measurement',
  'aggregationMethod',
  'factTable',
  'aggregationThreshold',
  'aggregationCap',
  'replaceMissingWithZero',
  'filter',
];

const buildAverageAggregation = ({
  measurement,
  aggregationMethod = MetricsV1AggregationType.AggregationTypeSum,
  aggregationThreshold,
  aggregationCap,
}: AverageCodeSnippetProps): string => {
  const aggLabel =
    aggregationUtils.getAggregationTypeSqlLabel(aggregationMethod);
  const value = measurement || '*';
  const base = aggregationThreshold
    ? `IF(${aggLabel}(${value}) ${
        aggregationUtils.getAggregationThresholdDirectionDetails(
          aggregationThreshold.direction,
        ).label
      } ${aggregationThreshold.threshold.value}, 1, 0)`
    : `${aggLabel}(${value})`;
  return applyCap(base, aggregationCap);
};

const averageInner = (cfg: AverageCodeSnippetProps, alias: string): string => {
  const entity = cfg.entity || 'entity';
  const factTable = cfg.factTable || 'fact_table';
  const aggregation = buildAverageAggregation(cfg);
  const aliased =
    alias === 'measurement'
      ? coalesceToZeroIf(aggregation, cfg.replaceMissingWithZero)
      : aggregation;
  return `SELECT
    ${entity},
    ${aliased} as ${alias}
  FROM ${factTable}${filterToWhereClause(cfg.filter)}
  GROUP BY ${entity}`;
};

export const averageCodeSnippet = (
  cfg: AverageCodeSnippetProps = {},
): string => `WITH measurements AS (
  ${averageInner(cfg, 'measure')}
)

SELECT
  AVG(${coalesceToZeroIf('measure', cfg.replaceMissingWithZero)}) as metric
FROM measurements`;

export const averageMeasurementSnippet = (
  cfg: AverageCodeSnippetProps = {},
): string => averageInner(cfg, 'measurement');
